import React, { useState, useEffect } from 'react';
import ModalWrapper from './ModalWrapper';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

export default function UserManagement({ open, onClose, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  
  // Password reset state
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  
  // Email editing state
  const [editEmailUser, setEditEmailUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [editEmailError, setEditEmailError] = useState('');
  
  // User activation toggle state
  const [togglingActivation, setTogglingActivation] = useState(null); // userId being toggled

  // Fetch users when modal opens
  useEffect(() => {
    if (open && currentUser?.role === 'administrator') {
      fetchUsers();
    }
  }, [open, currentUser]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users`, {
        mode: 'cors',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    setAddUserError('');

    // Frontend validation for @signseal.co.za domain
    if (!newUser.email.toLowerCase().endsWith('@signseal.co.za')) {
      setAddUserError('Only email addresses from @signseal.co.za domain are allowed');
      setAddingUser(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newUser),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add user');
      }

      // Clear form and refresh users list
      setNewUser({ email: '', password: '', name: '', role: 'user' });
      setShowAddUser(false);
      fetchUsers();

    } catch (error) {
      setAddUserError(error.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      // Refresh users list
      fetchUsers();
    } catch (error) {
      setError(error.message);
    }
  };

  // Update user email
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setUpdatingEmail(true);
    setEditEmailError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users/${editEmailUser.id}/email`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email: newEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update email');
      }

      // Success - refresh users list and close form
      fetchUsers();
      setEditEmailUser(null);
      setNewEmail('');
      alert('Email updated successfully!');
    } catch (error) {
      console.error('Error updating email:', error);
      setEditEmailError(error.message);
    } finally {
      setUpdatingEmail(false);
    }
  };

  // Toggle user activation status
  const handleToggleActivation = async (user) => {
    setTogglingActivation(user.id);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users/${user.id}/activation`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to toggle user activation');
      }

      // Success - refresh users list
      fetchUsers();
      alert(data.message);
    } catch (error) {
      console.error('Error toggling user activation:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setTogglingActivation(null);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResettingPassword(true);
    setResetPasswordError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users/${resetPasswordUser.id}/password`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      // Clear form and close reset modal
      setNewPassword('');
      setResetPasswordUser(null);
      
      // Show appropriate success message based on response
      if (data.isDeactivation) {
        alert(`User ${resetPasswordUser.name} has been deactivated. They cannot log in but audit logs are preserved.`);
      } else {
        alert(`Password reset successfully for ${resetPasswordUser.name}`);
      }

    } catch (error) {
      setResetPasswordError(error.message);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleClose = () => {
    setShowAddUser(false);
    setNewUser({ email: '', password: '', name: '', role: 'user' });
    setAddUserError('');
    setError('');
    // Reset password reset state
    setResetPasswordUser(null);
    setNewPassword('');
    setResetPasswordError('');
    onClose();
  };

  if (!open) return null;

  // Only show to administrators
  if (currentUser?.role !== 'administrator') {
    return (
      <ModalWrapper open={open} onClose={handleClose} zIndex={50}>
        <div style={{ textAlign: 'center', padding: 20 }}>
          <h2 style={{ color: '#d32f2f', marginBottom: 16 }}>Access Denied</h2>
          <p>Only administrators can access user management.</p>
          <button onClick={handleClose} style={{
            padding: '8px 16px',
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            marginTop: 16
          }}>
            Close
          </button>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper open={open} onClose={handleClose} zIndex={50} minWidth={800} maxWidth={900}>
      <h2 style={{
        color: '#1976d2',
        fontWeight: 800,
        marginBottom: 20,
        fontSize: 24,
        letterSpacing: 1
      }}>
        User Management
      </h2>

      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          border: '1px solid #ffcdd2'
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <button
          onClick={() => setShowAddUser(!showAddUser)}
          style={{
            padding: '10px 20px',
            background: '#4caf50',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
          }}
        >
          {showAddUser ? 'Cancel' : '+ Add New User'}
        </button>
      </div>

      {showAddUser && (
        <div style={{
          background: '#f8fafc',
          padding: 20,
          borderRadius: 12,
          marginBottom: 20,
          border: '1px solid #e0e0e0'
        }}>
          <h3 style={{ marginBottom: 16, color: '#333' }}>Add New User</h3>
          <form onSubmit={handleAddUser}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Name:</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Email:</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Password:</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Role:</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="user">User</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>
            </div>
            {addUserError && (
              <div style={{
                background: '#ffebee',
                color: '#c62828',
                padding: '8px 12px',
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 14
              }}>
                {addUserError}
              </div>
            )}
            <button
              type="submit"
              disabled={addingUser}
              style={{
                padding: '8px 16px',
                background: addingUser ? '#ccc' : '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: addingUser ? 'not-allowed' : 'pointer'
              }}
            >
              {addingUser ? 'Adding...' : 'Add User'}
            </button>
          </form>
        </div>
      )}

      {resetPasswordUser && (
        <div style={{
          background: '#fff3e0',
          padding: 20,
          borderRadius: 12,
          marginBottom: 20,
          border: '2px solid #ff9800'
        }}>
          <h3 style={{ marginBottom: 16, color: '#e65100' }}>Reset Password for {resetPasswordUser.name}</h3>
          <form onSubmit={handleResetPassword}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>New Password:</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters) or type 'deactivate' to deactivate user"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
              <div style={{
                fontSize: 12,
                color: '#666',
                marginTop: 4,
                fontStyle: 'italic'
              }}>
                💡 <strong>Tip:</strong> Type 'deactivate' to deactivate user (preserves audit logs but prevents login)
              </div>
            </div>
            {resetPasswordError && (
              <div style={{
                background: '#ffebee',
                color: '#c62828',
                padding: '8px 12px',
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 14
              }}>
                {resetPasswordError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="submit"
                disabled={resettingPassword || (newPassword.length > 0 && newPassword.length < 6 && newPassword.toLowerCase() !== 'deactivate')}
                style={{
                  padding: '8px 16px',
                  background: resettingPassword || (newPassword.length > 0 && newPassword.length < 6 && newPassword.toLowerCase() !== 'deactivate') ? '#ccc' : '#ff9800',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: resettingPassword || (newPassword.length > 0 && newPassword.length < 6 && newPassword.toLowerCase() !== 'deactivate') ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {resettingPassword ? 'Resetting...' : '🔑 Reset Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setResetPasswordUser(null);
                  setNewPassword('');
                  setResetPasswordError('');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Email editing form */}
      {editEmailUser && (
        <div style={{
          background: '#f0f8ff',
          padding: 20,
          borderRadius: 12,
          marginBottom: 20,
          border: '1px solid #2196f3'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#1976d2' }}>
            ✏️ Edit Email for {editEmailUser.name}
          </h3>
          <form onSubmit={handleUpdateEmail}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>New Email Address:</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                placeholder="Enter new email address"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
            </div>
            {editEmailError && (
              <div style={{
                background: '#ffebee',
                color: '#c62828',
                padding: '8px 12px',
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 14
              }}>
                {editEmailError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="submit"
                disabled={updatingEmail || !newEmail || newEmail === editEmailUser.email}
                style={{
                  padding: '8px 16px',
                  background: updatingEmail || !newEmail || newEmail === editEmailUser.email ? '#ccc' : '#2196f3',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: updatingEmail || !newEmail || newEmail === editEmailUser.email ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {updatingEmail ? 'Updating...' : '✏️ Update Email'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditEmailUser(null);
                  setNewEmail('');
                  setEditEmailError('');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{
        maxHeight: 400,
        overflowY: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: 8
      }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center' }}>Loading users...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Email</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Role</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Created</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isActive = user.passwordHash !== null;
                return (
                <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ 
                    padding: '12px 16px',
                    color: isActive ? '#333' : '#999',
                    fontStyle: isActive ? 'normal' : 'italic'
                  }}>
                    {user.name} {!isActive && '(Inactive)'}
                  </td>
                  <td style={{ 
                    padding: '12px 16px',
                    color: isActive ? '#333' : '#999'
                  }}>
                    {user.email}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={user.id === currentUser?.id}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        fontSize: 12,
                        background: user.id === currentUser?.id ? '#f5f5f5' : '#fff'
                      }}
                    >
                      <option value="user">User</option>
                      <option value="administrator">Administrator</option>
                    </select>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#666' }}>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          setEditEmailUser(user);
                          setNewEmail(user.email);
                        }}
                        style={{
                          padding: '4px 8px',
                          background: '#2196f3',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        ✏️ Edit Email
                      </button>
                      <button
                        onClick={() => handleToggleActivation(user)}
                        disabled={togglingActivation === user.id}
                        style={{
                          padding: '4px 8px',
                          background: togglingActivation === user.id ? '#ccc' : (isActive ? '#f44336' : '#4caf50'),
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          fontSize: 12,
                          cursor: togglingActivation === user.id ? 'not-allowed' : 'pointer',
                          fontWeight: 600
                        }}
                      >
                        {togglingActivation === user.id ? '⏳' : (isActive ? '🚫 Deactivate' : '✅ Activate')}
                      </button>
                      <button
                        onClick={() => setResetPasswordUser(user)}
                        style={{
                          padding: '4px 8px',
                          background: '#ff9800',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        🔑 Reset Password
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 20, textAlign: 'right' }}>
        <button
          onClick={handleClose}
          style={{
            padding: '10px 20px',
            background: '#fff',
            color: '#1976d2',
            border: '2px solid #1976d2',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
    </ModalWrapper>
  );
}
